/**
 * @author Jersson Morales
 * @Copyright (c) Infotrack. All rights reserved.
 */
import { useProveedor } from '@infotrack/presentacion-componentes/proveedorEstado';
import Texto from '@infotrack/presentacion-componentes/texto';
import { TipoFormulario } from '@infotrack/presentacion-transversales/interfacesComunes';
import {
    manejadorDialogoGlobal,
    manejadorIndicadorCargaGlobal,
} from '@infotrack/presentacion-utilitarios/manejadoresComponentes';
import IDivisionesDocumentosDefecto from 'Infotrack@Modelos/smartStock/divisionesDocumentosDefecto/entidades/divisionesDocumentosDefecto';
import { IEstadoGlobal } from 'Infotrack@Reductores/interfacesReductores';
import React from 'react';
import { connect } from 'react-redux';
import DivisionesDocumentosDefectoCM from '../controladorModelo/divisionesDocumentosDefectoCM';
import EntidadesDivisionesDocumentoCM from '../controladorModelo/entidadesDivisionesDocumentoCM';
import { acciones, IEstadoGestionBodegas } from '../reductorGestionBodegas';
import { IFormularioDivisionesDocumentosDefectoCVProps } from '../vista/constantes';
import DivisionesDocumentosDefectoVista from '../vista/tablaDivisionesDocumentosDefectoVista';

const divisionesDocumentosDefectoCM = new DivisionesDocumentosDefectoCM();
const entidadesDivisionesDocumentoCM = new EntidadesDivisionesDocumentoCM();

const TableDivisionesDocumentosDefectoCV: React.FunctionComponent<IFormularioDivisionesDocumentosDefectoCVProps> = ({
    IdEmpresa,
    IdAgencia,
}) => {
    const [divisionesDocumentosDefectos, setDivisionesDocumentosDefectos] = React.useState<
        IDivisionesDocumentosDefecto[]
    >([]);
    const [{ bodega, recargarTablaDivionesPorDefecto, listaDivisiones, listaTiposDocumentos }, dispatch] = useProveedor<
        IEstadoGestionBodegas
    >();
    const cargaInicial = React.useRef(true);

    React.useEffect(() => {
        if (recargarTablaDivionesPorDefecto || cargaInicial.current) {
            cargarDivisionesDocumentosDefecto();
            if (recargarTablaDivionesPorDefecto) {
                dispatch({ type: acciones.RECARGAR_TABLA_DIVISIONES_POR_DEFECTO });
            }
        }
        cargaInicial.current = false;
    }, [recargarTablaDivionesPorDefecto]);

    React.useEffect(() => {
        dispatch({ type: acciones.ESTABLECER_LISTA_DIVISIONES_POR_DEFECTO, payload: divisionesDocumentosDefectos });
    }, [divisionesDocumentosDefectos]);

    const cargarDivisionesDocumentosDefecto = async () => {
        setDivisionesDocumentosDefectos(
            await divisionesDocumentosDefectoCM.consultaDivisionesPorDefectoCompuestaFiltrada({
                BodegaId: Number(bodega.BodegaId),
            })
        );
    };

    const manejarFormulario = (entidadDivisionesDocumentosDefecto?: IDivisionesDocumentosDefecto) => {
        dispatch({ type: acciones.ESTABLECER_DIVISIONES_POR_DEFECTO, payload: entidadDivisionesDocumentosDefecto });
        dispatch({ type: acciones.CAMBIAR_ESTADO_FORMULARIO_DIVISIONES_POR_DEFECTO });
    };

    const confirmarEliminacion = (divisionesDocumentosDefecto: IDivisionesDocumentosDefecto) => {
        manejadorDialogoGlobal({
            accionCancelar: () => manejadorDialogoGlobal({ estado: false }),
            accionConfirmar: () => eliminarDivisionesDocumentosDefecto(divisionesDocumentosDefecto),
            estado: true,
            mensaje: <Texto id={'alerta.confirmacionEliminacion'} />,
            mostrarCancelar: true,
            tipoDialogo: 'Advertencia',
            titulo: <Texto id="titulo.advertencia" />,
        });
    };

    const eliminarDivisionesDocumentosDefecto = (divisionesDocumentosDefecto: IDivisionesDocumentosDefecto) => {
        manejadorIndicadorCargaGlobal(true);
        manejadorDialogoGlobal({ estado: false });
        divisionesDocumentosDefectoCM.eliminarDivisionesDocumentosDefecto(divisionesDocumentosDefecto).then(() => {
            manejadorIndicadorCargaGlobal(false);
            dispatch({ type: acciones.RECARGAR_TABLA_DIVISIONES_POR_DEFECTO });
        });
    };

    const confirmarActivarDivisionDefecto = (divisionesDocumentosDefectos: IDivisionesDocumentosDefecto) => {
        manejadorDialogoGlobal({
            accionCancelar: () => manejadorDialogoGlobal({ estado: false }),
            accionConfirmar: () => manejadorSeleccionDivisionDefecto(divisionesDocumentosDefectos),
            estado: true,
            mensaje: 'Esta seguro de cambiar la división por defecto para el documento ',
            mostrarCancelar: true,
            tipoDialogo: 'Advertencia',
            titulo: <Texto id="titulo.advertencia" />,
        });
    };

    const manejadorSeleccionDivisionDefecto = async (
        divisionesDocumentosDefectosSelec: IDivisionesDocumentosDefecto
    ) => {
        manejadorIndicadorCargaGlobal(true);
        manejadorDialogoGlobal({ estado: false });
        divisionesDocumentosDefectosSelec.DivisionDefecto = true;
        await divisionesDocumentosDefectoCM.activarDivisionPorDefecto(divisionesDocumentosDefectosSelec).then(() => {
            manejadorIndicadorCargaGlobal(false);
            dispatch({ type: acciones.RECARGAR_TABLA_DIVISIONES_POR_DEFECTO });
        });
    };

    return (
        <DivisionesDocumentosDefectoVista
            divisionesDocumentosDefectos={divisionesDocumentosDefectos}
            cargarDivisionesDocumentosDefecto={cargarDivisionesDocumentosDefecto}
            confirmarEliminacion={confirmarEliminacion}
            manejarFormulario={manejarFormulario}
            confirmarActivarDivisionDefecto={confirmarActivarDivisionDefecto}
        />
    );
};
const estadoAPropiedades = ({
    estadoAutenticacion: { usuarioInformacion, agenciaActual },
}: IEstadoGlobal): IFormularioDivisionesDocumentosDefectoCVProps => ({
    IdEmpresa: usuarioInformacion!.IdEmpresa,
    IdAgencia: agenciaActual!.IdAgencia,
});

export default connect<IFormularioDivisionesDocumentosDefectoCVProps, null, any, IEstadoGlobal>(
    estadoAPropiedades,
    null
)(TableDivisionesDocumentosDefectoCV);
